import React from 'react'
import { connect } from 'react-redux'
import Dialog from '@mui/material/Dialog'
import { withStyles } from '@mui/styles'
import WebStorageService from '@/modules/_services/webstorage.service'
import WebStorageKey from '@/enums/web-storage-key.enum'
import request from '@/modules/base/util/request'
import { isEmpty } from 'lodash'
import clsx from 'clsx'

import settingsConfig from '@/modules/settings/config'
import { paymentTypes, subscriptionTypes } from '@/modules/base/util/constants'

import { Wizard, WizardStep } from '@/modules/base/components/wizard/index'

import { userLoggedOut } from '@/redux/ducks/global'

import lang from '@/lang'
import LimitReachedView from './limit-reached-view/limit-reached-view'
import BillingForm from './billing-form/billing-form'
import OrderOverview from './order-overview/order-overview'
import SuccessView from './success-view/success-view'
import SubscriptionExpiredView from './subscription-expired-view/subscription-expired-view'
import SubscriptionSelection from './subscription-selection/subscription-selection.js' // OMO und GMI
import SubscriptionSelectionMB from './subscription-selection/subscription-selection.meinbuero.js' // MBO
import { updateSubscriptionDetails } from '@/redux/ducks/subscription/details'
import { checkIsAdminRole } from '@/modules'
import { trackOmoSubscription, trackOmoSubscriptionSelection } from '@/modules/base/helpers/omoSubscriptionTracker'
import { isMeinBuero } from '@/utils/build-env'
import BeautifulLimitReachedView from './limit-reached-view/beautiful-limit-reached-view'
import CheckIcon from '@assets/images/icons/check.svg'
import { ButtonIconSquare, CancelButton } from '@/modules/base/components/buttons'
import SubscriptionTypeSelection from '@/modules/subscription/subscription-type-selection'
import SubscriptionTypeSelectionMb from '@/modules/subscription/subscription-type-selectionMb'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { withMobileScreen } from '@/modules/_helpers/detectMobile'
import CloseIcon from '@assets/images/icons/v2/close.svg'
import ConfirmEmailModal from '@/modules/account/components/confirm-email/confirm-email-modal'
import { navigate } from '@/modules/layout/util/useInterceptedNavigate'
import { PRODUCT_ROUTE_MAP } from '@/modules/moduleSubscription/constants'

const isMeinbuero = isMeinBuero()

const { DEBIT } = paymentTypes
const LIMIT_REACHED_VIEW = 'limitReachedView'
const BEAUTIFUL_LIMIT_REACHED_VIEW = 'beautifulLimitReachedView'
const SUBSCRIPTION_EXPIRED_VIEW = 'subscriptionExpiredView'
const TYPE_SELECTION = 'typeSelection'
const SUBSCRIPTION_SELECTION = 'subscriptionSelection'
const BILLING_FORM = 'billingForm'
const ORDER_OVERVIEW = 'orderOverview'
const SUCCESS_VIEW = 'successView'
const OLP_PLAN_INFO = 'oldPlanInfo'

const SMALL_SIZED_STEPS = [LIMIT_REACHED_VIEW, OLP_PLAN_INFO]
const OLD_PLANS = [
  subscriptionTypes.PREMIUM,
  subscriptionTypes.PREMIUM_CHRISTMAS_DISCOUNT,
  subscriptionTypes.S,
  subscriptionTypes.M,
  subscriptionTypes.L,
]

const DEFAULT_BILLING_DATA = {
  salutation: 'Herr',
  title: '',
  bankname: '',
  bankAccountBic: '',
  bankAccountHolder: '',
  bankAccountIBAN: '',
  firstName: '',
  lastName: '',
  companyName: '',
  street: '',
  zipCode: '',
  sepaMandate: true,
  city: '',
}

class UpgradeModal extends React.Component {
  constructor(props) {
    super(props)

    const { data = null } = props
    this.state = {
      openConfirmEmailModal: true,
      activeStep: 0,
      steps: [],
      subscriptionType: props.subscriptionType,
      selectedSubscription: null,
      selectedPayOption: DEBIT,
      selectedPayInterval: null,
      isBillingFormValid: false,
      defaultBillingData: DEFAULT_BILLING_DATA,
      isIBANValid: false,
      data,
    }

    this.stepArray = [SUBSCRIPTION_SELECTION, BILLING_FORM, ORDER_OVERVIEW, SUCCESS_VIEW]
    this.onModalClose = this.onModalClose.bind(this)
    this.onLogout = this.onLogout.bind(this)
    this.onCancel = this.onCancel.bind(this)
    this.onPayIntervalClick = this.onPayIntervalClick.bind(this)
    this.onContinueSubscription = this.onContinueSubscription.bind(this)
    this.onBillingFormBack = this.onBillingFormBack.bind(this)
    this.onContinueBillingForm = this.onContinueBillingForm.bind(this)
    this.onContinueOrderOverview = this.onContinueOrderOverview.bind(this)
    this.onOrderOverviewBack = this.onOrderOverviewBack.bind(this)
    this.onUpgradeClick = this.onUpgradeClick.bind(this)
    this.onCloseConfirmEmailModal = this.onCloseConfirmEmailModal.bind(this)
    this.finishUpgrade = this.finishUpgrade.bind(this)
    this.validateBillingData = this.validateBillingData.bind(this)
    this.onBillingDataChange = this.onBillingDataChange.bind(this)
    this.onKindButtonClicked = this.onTypeButtonClicked.bind(this)
    this.redirectToShop = this.redirectToShop.bind(this)
  }

  componentDidMount() {
    const { steps } = this.state
    const {
      subscriptionType: subscriptionTypeProps,
      trialExpired,
      limitReached,
      skipSubscriptionSelection,
      preSelectedSubscription,
      subscriptionChange,
      classes,
      firstName,
      lastName,
      subscriptions,
    } = this.props
    const newSteps = [...steps]

    if (!subscriptionTypeProps) {
      this.stepArray.unshift(TYPE_SELECTION)
    }

    if (trialExpired) {
      this.stepArray.unshift(SUBSCRIPTION_EXPIRED_VIEW)
      newSteps.unshift(
        <WizardStep key={SUBSCRIPTION_EXPIRED_VIEW} customClass={classes.wizard}>
          <SubscriptionExpiredView onUpgradeClick={this.redirectToShop} onAbort={() => this.onCancel(SUBSCRIPTION_EXPIRED_VIEW)} firstName={firstName} lastName={lastName} />
        </WizardStep>,
      )
    }

    // general limit handling
    if (limitReached) {
      const isPlanLimit = limitReached.toLowerCase().search('notinplan') !== -1
      const limit = limitReached.split('.')[limitReached.split('.').length - 1]
      const limitations = isPlanLimit ? lang.limitations.notInPlan[limit] : lang.limitations[limit]

      const title = limitations?.title || limitations.default.title
      const description = limitations?.description || limitations.default.description
      let activePlan
      let documentCount
      let subTitle
      if (limit === 'documentCount') {
        activePlan = subscriptions[0].title
        documentCount = subscriptions[0].limitations.documentCount
        subTitle = subscriptions.length > 1 ? limitations.multiplePlanSubtitle : [<div dangerouslySetInnerHTML={{ __html: limitations.subTitle(activePlan, documentCount) }}></div>]
      } else {
        subTitle = limitations.subTitle
      }

      if (subTitle) {
        this.stepArray.unshift(BEAUTIFUL_LIMIT_REACHED_VIEW)
        newSteps.unshift(
          <WizardStep key={BEAUTIFUL_LIMIT_REACHED_VIEW}>
            <BeautifulLimitReachedView
              title={title}
              description={description}
              subTitle={subTitle}
              onAbort={() => this.onCancel(BEAUTIFUL_LIMIT_REACHED_VIEW)}
              onContinue={this.onUpgradeClick}
              limitations={limitations}
              limit={limitReached}>
            </BeautifulLimitReachedView>
          </WizardStep>,
        )
      } else {
        this.stepArray.unshift(LIMIT_REACHED_VIEW)
        newSteps.unshift(
          <WizardStep key={LIMIT_REACHED_VIEW}>
            <LimitReachedView
              title={title}
              description={description}
              onAbort={() => this.onCancel(LIMIT_REACHED_VIEW)}
              onContinue={this.redirectToShop}
            />
          </WizardStep>,
        )
      }

    }

    if (skipSubscriptionSelection && !isMeinbuero) {
      const { subscription, selectedPeriod } = preSelectedSubscription
      const { prices } = subscription
      const selectedPayInterval = prices.find(price => price.period === selectedPeriod)
      const activeStep = subscriptionChange ? this.stepArray.indexOf(ORDER_OVERVIEW) : this.stepArray.indexOf(BILLING_FORM)

      this.setState({
        activeStep,
        selectedSubscription: subscription,
        selectedPayInterval: { period: selectedPeriod, ...selectedPayInterval.values[0] },
      })
    }

    request(`${settingsConfig.account}`, { auth: true, isConcurrent: true }).then((response) => {
      const {
        body: {
          data: {
            accountEmail,
            companyAddress,
            ownerFirstName: firstName,
            ownerLastName: lastName,
            bankAccountHolder: accountHolder,
            bankAccountIBAN: accountIBAN,
            bankAccountBic,
            bankname,
          },
        },
      } = response

      const activeSubscription = subscriptions.find(sub => !isEmpty(sub.billingData)) || subscriptions?.[0]
      request(settingsConfig.getSubscriptionBillingData(activeSubscription?.id), { auth: true }).then((response) => {
        const {
          body: {
            data: {
              bankAccountIBAN: billingIBAN,
              bankAccountHolder: billingHolder,
              city,
              zipCode,
              street,
              firstName: billingDataFirstName,
              lastName: billingDataLastName,
            },
          },
        } = response

        const newBankAccountHolder = billingHolder?.length ? billingHolder : accountHolder
        const newBankAccountIBAN = billingIBAN?.length ? billingIBAN : accountIBAN
        const defaultBillingData = {
          ...DEFAULT_BILLING_DATA,
          ...companyAddress,
          email: accountEmail,
          firstName: billingDataFirstName || firstName,
          lastName: billingDataLastName || lastName,
          city: city || companyAddress.city,
          zipCode: zipCode || companyAddress.zipCode,
          street: street || companyAddress.street,
          bankAccountHolder: newBankAccountHolder,
          bankAccountIBAN: newBankAccountIBAN,
          bankAccountBic,
          bankname,
        }

        this.setState(
          (state) => ({
            defaultBillingData,
            isIBANValid: Boolean(newBankAccountIBAN),
            data: {
              ...state.data,
              ...defaultBillingData,
              paymentType: state.selectedPayOption,
              isIBANValid: Boolean(newBankAccountIBAN), // set isIBANValid true because the iban was already checked
            },
          }),
          () => {
            this.validateBillingData(defaultBillingData)
          },
        )
      })
    })

    this.setState({ steps: newSteps })
  }

  componentWillUnmount() {
    this.setState({ selectedPayOption: null, data: null })
  }

  validateBillingData(data) {
    const { billingFormFields } = settingsConfig
    const { isBillingFormValid } = this.state
    const { isIBANValid } = this.state
    const invalidInputs = billingFormFields.filter((prop) => {
      if (!data[prop]) {
        return prop
      }
    })

    const isNewBillingFormValid = invalidInputs.length <= 0 && isIBANValid
    if (isBillingFormValid !== isNewBillingFormValid) {
      this.setState({ isBillingFormValid: isNewBillingFormValid })
    }
  }

  onCancel(key) {
    switch (key) {
      case ORDER_OVERVIEW:
        trackOmoSubscription(
          'click_bestelluebersicht',
          'bestelluebersicht_abbrechen',
        )
        break
      case BILLING_FORM:
        trackOmoSubscription(
          'click_abrechnungsinformation',
          'abrechnungsinformation_abbrechen',
        )
        break
      case SUBSCRIPTION_SELECTION:
        trackOmoSubscription(
          'click_planauswahl_abbrechen',
          'planauswahl_abbrechen',
        )
        break
      case SUBSCRIPTION_EXPIRED_VIEW:
        trackOmoSubscription(
          'click_testphase',
          'testphase_lesemodus',
        )
        break
      case LIMIT_REACHED_VIEW:
      case BEAUTIFUL_LIMIT_REACHED_VIEW:
        trackOmoSubscription(
          'click_limit',
          'limit_reached_modal_readMode',
        )
        break
    }

    const { trialExpired } = this.props
    const readOnlyConfirmed = WebStorageService.getItem(WebStorageKey.READ_ONLY_CONFIRMED)
    if (trialExpired && !readOnlyConfirmed) {
      return
    }

    this.setState({ activeStep: this.stepArray.indexOf(SUBSCRIPTION_SELECTION) }, () => {
      this.onModalClose()
    })
  }

  onCloseConfirmEmailModal() {
    this.setState({ openConfirmEmailModal: false })
  }

  onUpgradeClick() {
    const { subscriptionType } = this.props

    trackOmoSubscription(
      'click_demo-modus',
      'testphase_beenden_banner',
    )

    this.setState({ activeStep: this.stepArray.indexOf(subscriptionType ? SUBSCRIPTION_SELECTION : TYPE_SELECTION) })
  }

  redirectToShop() {
    const { subscriptionType, onClose } = this.props
    navigate(`/shop/${subscriptionType ? PRODUCT_ROUTE_MAP[subscriptionType] : ''}`)
    onClose()
  }

  onPayIntervalClick(interval) {
    this.setState({
      selectedPayInterval: interval,
      activeStep: this.stepArray.indexOf(BILLING_FORM),
    })
  }

  onLogout(key) {
    switch (key) {
      case ORDER_OVERVIEW:
        trackOmoSubscription(
          'click_bestelluebersicht',
          'bestelluebersicht_abmelden_lesemodus',
        )
        break
      case BILLING_FORM:
        trackOmoSubscription(
          'click_abrechnungsinformation',
          'abrechnungsinformation_abmelden_lesemodus',
        )
        break
      case SUBSCRIPTION_SELECTION:
        trackOmoSubscription(
          'click_planauswahl_abbrechen',
          'planauswahl_abmelden_lesemodus',
        )
        break
    }
    const { onClose, logout } = this.props
    logout()
    onClose()
  }

  onModalClose() {
    const { onClose, trialExpired } = this.props
    const readOnlyConfirmed = WebStorageService.getItem(WebStorageKey.READ_ONLY_CONFIRMED)
    if (ENV.product !== 'meinbuero' && trialExpired && !readOnlyConfirmed) {
      return
    }

    onClose()
  }

  onContinueSubscription(data) {
    const { isBillingFormValid } = this.state
    const { subscriptionChange, skipPayment } = this.props
    const { subscription, selectedPeriod } = data
    const { prices } = subscription

    trackOmoSubscriptionSelection(subscription)

    const selectedPayInterval = prices.find((price) => price.period === selectedPeriod)

    let activeStep = this.stepArray.indexOf(ORDER_OVERVIEW)

    if (subscriptionChange || skipPayment) {
      if (!isBillingFormValid) activeStep = this.stepArray.indexOf(BILLING_FORM)
    } else {
      activeStep = this.stepArray.indexOf(BILLING_FORM)
    }

    this.setState({
      selectedSubscription: subscription,
      selectedPayInterval: { period: selectedPeriod, ...selectedPayInterval.values[0] },
      activeStep,
    })
  }

  onContinueBillingForm(billingData) {
    const { selectedPayOption, data } = this.state

    trackOmoSubscription(
      'click_abrechnungsinformation',
      'abrechnungsinformation_weiter',
    )

    this.setState({
      activeStep: this.stepArray.indexOf(ORDER_OVERVIEW),
      data: { ...data, ...billingData, paymentType: selectedPayOption },
    })
  }

  onBillingFormBack() {
    const { skipSubscriptionSelection } = this.props
    if (skipSubscriptionSelection) {
      return this.onCancel(BILLING_FORM)
    }

    trackOmoSubscription(
      'click_abrechnungsinformation',
      'abrechnungsinformation_zurueck',
    )

    this.setState({
      activeStep: this.stepArray.indexOf(SUBSCRIPTION_SELECTION),
    })
  }

  onContinueOrderOverview() {
    trackOmoSubscription(
      'click_bestelluebersicht',
      'bestelluebersicht_zahlungspflichtig_bestellen',
    )

    this.setState({ activeStep: this.stepArray.indexOf(SUCCESS_VIEW) }, () => {
      updateSubscriptionDetails()
    })
  }

  onOrderOverviewBack() {
    trackOmoSubscription(
      'click_bestelluebersicht',
      'bestelluebersicht_zurueck',
    )

    const { subscriptionChange } = this.props
    const lastStep = subscriptionChange
      ? this.stepArray.indexOf(SUBSCRIPTION_SELECTION)
      : this.stepArray.indexOf(BILLING_FORM)

    this.setState({ activeStep: lastStep })
  }

  finishUpgrade() {
    const { onClose, onUpgraded } = this.props
    this.setState({ activeStep: this.stepArray.indexOf(SUBSCRIPTION_SELECTION) }, () => {
      onClose()
      onUpgraded()
    })
  }

  onBillingDataChange(newData) {
    const { bankAccountHolder, bankAccountIBAN, bankAccountBic } = newData
    this.setState((state) => ({
      ...state,
      data: { ...state.data, ...{ bankAccountHolder, bankAccountIBAN, bankAccountBic } },
    }))
  }

  onTypeButtonClicked(type) {
    this.setState({
      subscriptionType: type,
    })
  }

  render() {
    const {
      classes,
      open,
      trialExpired,
      activeSubscription,
      overrideTexts,
      actualMarketplaceId,
      noRight,
      hasEmailAccess,
      onClose,
      groupedSubscriptionTypes,
      subscriptions,
      isMobileScreen,
      dataQsId,
    } = this.props
    const {
      steps,
      activeStep,
      data,
      selectedSubscription,
      selectedPayInterval,
      defaultBillingData,
      isBillingFormValid,
      isIBANValid,
      openConfirmEmailModal,
      subscriptionType,
    } = this.state
    const isOldPlan = OLD_PLANS.includes(activeSubscription)
    const subscriptionTypeTypesLabel = subscriptions?.map(s => s.typeOfSubscription).join(', ') || ''

    const orderData = data
      ? { ...data, subscription: selectedSubscription, paymentInterval: selectedPayInterval }
      : null

    const finalSteps = [
      ...steps,
      this.stepArray.includes(TYPE_SELECTION) && <WizardStep key={TYPE_SELECTION} customClass={classes.wizardStep}>
        <div className={classes.groupedSubscriptionContainer}>
          {ENV.product !== 'meinbuero'
            ? <SubscriptionTypeSelection context='upgrade' useBulletPointIcons={false} groupedSubscriptionTypes={groupedSubscriptionTypes} kind='single' onChange={(sType) => this.onTypeButtonClicked(sType)} />
            : <SubscriptionTypeSelectionMb groupedSubscriptionTypes={groupedSubscriptionTypes} kind='single' onChange={(sType) => this.onTypeButtonClicked(sType)} />
          }
          <div className={classes.actionWrapper}>
            <CancelButton
              key='cancel-button'
              label={lang.cancelButtonLabel}
              onClick={onClose} />
            <ButtonIconSquare
              customClass={classes.continueButton}
              icon={CheckIcon}
              label={lang.continueButtonLabel}
              onClick={() => this.setState({ activeStep: this.stepArray.indexOf(SUBSCRIPTION_SELECTION) })}
              disabled={!subscriptionType}
            />
          </div>
        </div>
      </WizardStep>,
      <WizardStep
        key={SUBSCRIPTION_SELECTION}
        customClass={classes.wizard}
        onNext={this.onContinueSubscription}
        onPrevious={this.stepArray.includes(TYPE_SELECTION) ? () => this.setState({ activeStep: this.stepArray.indexOf(TYPE_SELECTION) }) : undefined}
      >
        {ENV.product === 'meinbuero' && subscriptionType !== 'gmi'
          ? (
            <SubscriptionSelectionMB
              readOnly={noRight}
              product={groupedSubscriptionTypes[subscriptionType] || {}}
              hasPrevious={this.stepArray.includes(TYPE_SELECTION)}
              actualMarketplaceId={actualMarketplaceId}
              isOldPlan={isOldPlan}
              onClose={this.onModalClose}
              open={activeStep === this.stepArray.indexOf(SUBSCRIPTION_SELECTION)}
            />
          )
          : (
            <SubscriptionSelection
              readOnly={noRight}
              hasPrevious={this.stepArray.includes(TYPE_SELECTION)}
              subscriptionTypes={groupedSubscriptionTypes[subscriptionType] || []}
              isOldPlan={isOldPlan}
              trialExpired={trialExpired}
              subscriptionType={subscriptionType}
              activeSubscription={activeSubscription}
              overrideTexts={overrideTexts}
              onCancel={() => this.onCancel(SUBSCRIPTION_SELECTION)}
              onLogout={() => this.onLogout(SUBSCRIPTION_SELECTION)}
            />
          )}
      </WizardStep>,
      <WizardStep
        key={BILLING_FORM}
        customClass={classes.wizard}
        onNext={this.onContinueBillingForm}
        onPrevious={this.onBillingFormBack}
      >
        <BillingForm
          trialExpired={trialExpired}
          onCancel={() => this.onCancel(BILLING_FORM)}
          onLogout={() => this.onLogout(BILLING_FORM)}
          onPayIntervalClick={this.onPayIntervalClick}
          subscription={selectedSubscription}
          selectedPayInterval={selectedPayInterval}
          defaultData={defaultBillingData}
          validateForm={this.validateBillingData}
          isValid={isBillingFormValid}
          isIBANValid={isIBANValid}
          subscriptionType={subscriptionType}
          onIBANChange={(isIBANValid) => this.setState({ isIBANValid })}
        />
      </WizardStep>,
      <WizardStep
        key={ORDER_OVERVIEW}
        customClass={classes.wizard}
        onNext={this.onContinueOrderOverview}
        onPrevious={this.onOrderOverviewBack}
      >
        <OrderOverview
          data={orderData}
          billingData={defaultBillingData}
          trialExpired={trialExpired}
          subscriptionType={subscriptionType}
          onCancel={() => this.onCancel(ORDER_OVERVIEW)}
          onLogout={() => this.onLogout(ORDER_OVERVIEW)}
          onBillingDataChange={this.onBillingDataChange}
          selectedPeriod={this.state.selectedPayInterval?.period}
        />
      </WizardStep>,
      <WizardStep key={SUCCESS_VIEW} customClass={classes.wizardFinished}>
        <SuccessView onFinish={this.finishUpgrade} />
      </WizardStep>,
    ]

    let dialogClass = classes.container
    if (
      SMALL_SIZED_STEPS.includes(this.stepArray[activeStep])
      || (this.stepArray[activeStep] === SUBSCRIPTION_SELECTION && isOldPlan)
    ) {
      dialogClass = classes.smallContainer
    } else if (this.stepArray[activeStep] === SUBSCRIPTION_SELECTION && subscriptionType !== 'gmi') {
      dialogClass
        = ENV.product === 'meinbuero'
          ? classes.subscriptionSelectionContainerMBO
          : classes.container
    } else if (this.stepArray[activeStep] === SUBSCRIPTION_SELECTION && subscriptionType === 'gmi') {
      dialogClass = classes.gmiContainer
    } else if (this.stepArray[activeStep] === SUBSCRIPTION_EXPIRED_VIEW) {
      dialogClass = classes.expContainer
    } else if (this.stepArray[activeStep] === BEAUTIFUL_LIMIT_REACHED_VIEW) {
      dialogClass = classes.beautifulLimit
    } else if (this.stepArray[activeStep] === TYPE_SELECTION) {
      dialogClass = classes.autoContainer
    }

    return (
      hasEmailAccess || isMeinbuero
        ? (
          <Dialog
            maxWidth={isMeinbuero && isMobileScreen}
            disableEscapeKeyDown
            scroll="paper"
            classes={{
              paper: clsx(dialogClass, isMeinbuero && classes.fullscreen),
            }}
            open={open}
            data-qs-id={dataQsId}
            onClose={(event, reason) => {
              if (reason === 'backdropClick') {
                return false
              }

              this.onModalClose()
            }}
            ref={this.dialogRef}
          >
            {isMeinbuero && isMobileScreen && <SVGIcon svg={CloseIcon} width={20} height={20} className={classes.closeIcon} onClick={this.onModalClose} />}
            <Wizard activeIndex={activeStep} customClass={classes.wizard}>
              {finalSteps}
            </Wizard>
          </Dialog>
        )
        : (openConfirmEmailModal
            && <ConfirmEmailModal
              closeModal={this.onCloseConfirmEmailModal}
              open={openConfirmEmailModal}
              isModal
              gaData={{
                'eventCategory': 'DOI',
                'eventAction': 'Jetzt kaufen',
                'eventLabel': `DOI durchgeführt ${subscriptionTypeTypesLabel}`,
              }}
              title={lang.upgradeModal.confirmEmailTitle}
              fontSize={19}
            />)
    )
  }
}

UpgradeModal.defaultProps = {
  open: false,
  trialExpired: false,
}

const styles = (theme) => ({
  actionWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  continueButton: {
    marginLeft: 20,
    minWidth: 150,
  },
  boxWrapper: {
    paddingBottom: 20,
    display: 'flex',
    gap: 20,
    justifyContent: 'flex-start',
  },
  container: {
    width: 1200,
    height: 'calc(100vh - 100px)',
    padding: '0 30px 10px 30px',
    '@media screen and (min-height: 860px)': {
      padding: '10px 30px 30px 30px',
    },
    display: 'flex',
  },
  beautifulLimit: {
    width: 1200,
    height: 580,
    padding: 0,
  },
  expContainer: {
    padding: 0,
  },
  wizardFinished: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    paddingTop: 141,
  },
  subscriptionSelectionContainer: {
    width: 1200,
    padding: '0 30px 10px 30px',
    '@media screen and (min-height: 860px)': {
      padding: '10px 30px 30px 30px',
    },
  },
  subscriptionSelectionContainerMBO: {
    width: 1420,
    backgroundColor: theme.superlightGrey,

    [theme.devices.mobile]: {
      width: 'auto',
    },
  },
  autoContainer: {
    width: 'auto',
    height: 'calc(100vh - 100px)',
    padding: 30,
  },
  smallContainer: {
    width: '600px',
    maxWidth: '600px',
    height: 'auto',
    minHeight: '300px',
  },
  gmiContainer: {
    width: !theme.isMeinbuero ? '820px' : '700px',
    height: !theme.isMeinbuero ? '720px' : '600px',
    padding: '0 30px 10px 30px',
  },
  wizard: {
    height: '100%',
    overflowY: 'auto',

    '&::-webkit-scrollbar': {
      display: 'none',
    },

    '& .modal-footer': {
      display: 'flex',
      padding: 0,
      paddingTop: 20,
      height: '100%',
      alignItems: 'end',
      bottom: !theme.isMeinbuero ? '5%' : 0,
      background: theme.white,
    },
  },
  wizardStep: {
    width: 1200,
    height: '100%',

    [theme.devices.mobile]: {
      width: 'auto',
    },
  },
  groupedSubscriptionContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between',
  },
  fullscreen: {
    [theme.devices.mobile]: {
      height: '100vh',
      paddingBottom: 0,
      maxHeight: '100vh',
      width: '100%',
      margin: 0,
    },
  },
  closeIcon: {
    position: 'absolute',
    top: 20,
    right: 20,
    zIndex: 2000,
  },
})

const mapStateToProps = state => {
  const { user: { info: userInfo }, subscription: { details: { subscriptions } } } = state
  return {
    noRight: !checkIsAdminRole(userInfo),
    // DO-13065: upgrade lock follows hasEmailAccess, the cosmetic hints stay on hasConfirmedEmail
    hasEmailAccess: userInfo.hasEmailAccess,
    email: userInfo.email,
    firstName: userInfo.firstName,
    lastName: userInfo.lastName,
    subscriptions,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(userLoggedOut()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(withMobileScreen(UpgradeModal)))
